import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import type { InvoiceSettingsFormData } from '@/lib/schemas/invoice-settings-schemas'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Button } from '@/components/ui/button'
import { InvoiceSettingsFields } from '@/components/invoices/invoice-settings-fields'
import { InvoiceTemplateSettings } from '@/components/invoices/invoice-template-settings'
import { MisaAccountCard } from '@/components/invoices/misa-account-card'
import { useAuth } from '@/hooks/use-auth'
import {
    useInvoiceSettings,
    useUpdateInvoiceSettings,
} from '@/hooks/use-invoice-query'
import { createInvoiceSettingsSchema } from '@/lib/schemas/invoice-settings-schemas'
import { m } from '@/lib/paraglide/messages'

const EMPTY_VALUES: InvoiceSettingsFormData = {
    company_name: '',
    company_address: '',
    company_phone: '',
    company_tax_code: '',
    bank_name: '',
    bank_account: '',
    bank_holder: '',
    footer_note: '',
    vat_percent: 0,
    misa_ip_template_id: '',
    misa_inv_series: '',
}

function optional(value?: string) {
    return value || undefined
}

export function InvoiceSettingsForm() {
    const { data: settings, isLoading } = useInvoiceSettings()
    const update = useUpdateInvoiceSettings()
    const { hasPermission } = useAuth()
    const form = useForm<InvoiceSettingsFormData>({
        resolver: zodResolver(createInvoiceSettingsSchema()),
        defaultValues: EMPTY_VALUES,
    })

    useEffect(() => {
        if (!settings) return
        form.reset({
            company_name: settings.company_name,
            company_address: settings.company_address,
            company_phone: settings.company_phone,
            company_tax_code: settings.company_tax_code ?? '',
            bank_name: settings.bank_name ?? '',
            bank_account: settings.bank_account ?? '',
            bank_holder: settings.bank_holder ?? '',
            footer_note: settings.footer_note ?? '',
            vat_percent: settings.vat_percent,
            misa_ip_template_id: settings.misa_ip_template_id ?? '',
            misa_inv_series: settings.misa_inv_series ?? '',
        })
    }, [form, settings])

    const handleSubmit = form.handleSubmit(async (data) => {
        const template = data.misa_ip_template_id
            ? {
                  id: data.misa_ip_template_id,
                  series: data.misa_inv_series,
              }
            : {
                  id: settings?.misa_ip_template_id ?? '',
                  series: settings?.misa_inv_series ?? '',
              }
        await update.mutateAsync({
            ...data,
            company_tax_code: optional(data.company_tax_code),
            bank_name: optional(data.bank_name),
            bank_account: optional(data.bank_account),
            bank_holder: optional(data.bank_holder),
            footer_note: optional(data.footer_note),
            misa_ip_template_id: template.id,
            misa_inv_series: template.series,
        })
    })

    if (isLoading) {
        return (
            <div className="text-sm text-muted-foreground">
                {m.common_loading()}
            </div>
        )
    }

    return (
        <div className="max-w-2xl space-y-6 page-pad">
            <div className="flex min-w-0 items-center gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <h1 className="text-xl font-semibold">
                    {m.invoice_settings_title()}
                </h1>
            </div>
            <FormProvider {...form}>
                <form noValidate onSubmit={handleSubmit} className="space-y-6">
                    <InvoiceSettingsFields />
                    <InvoiceTemplateSettings
                        savedTemplateID={settings?.misa_ip_template_id}
                        savedSeries={settings?.misa_inv_series}
                    />
                    {hasPermission('invoices.settings') ? (
                        <div className="flex justify-end">
                            <Button type="submit" disabled={update.isPending}>
                                {update.isPending
                                    ? m.common_saving()
                                    : m.invoice_save()}
                            </Button>
                        </div>
                    ) : null}
                </form>
                <MisaAccountCard />
            </FormProvider>
        </div>
    )
}
